class FitFinder extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.page = this.dataset.mode === 'page';
    // No product: general guide (page) or the drawer on a collection page.
    this.hasProduct = Boolean(this.dataset.productUrl);
    this.variants = JSON.parse(this.querySelector('[data-fit-variants]')?.textContent || 'null');
    // Translations arrive HTML-escaped (&amp;, &#39;), decode them for textContent.
    const decoder = document.createElement('textarea');
    const raw = JSON.parse(this.querySelector('[data-fit-strings]')?.textContent || '{}');
    this.strings = Object.fromEntries(
      Object.entries(raw).map(([key, value]) => {
        decoder.innerHTML = value;
        return [key, decoder.value];
      })
    );
    this.model = this.dataset.model || '';
    this.fitScore = Number(this.dataset.fitScore) || 0;
    this.lengthSelect = this.querySelector('[data-fit-length]');
    this.sizeEl = this.querySelector('[data-fit-size]');
    this.matchEl = this.querySelector('[data-fit-match]');
    this.reasonEl = this.querySelector('[data-fit-reason]');
    this.noteEl = this.querySelector('[data-fit-note]');
    this.widthEl = this.querySelector('[data-fit-width]');
    this.selectButton = this.querySelector('[data-fit-select]');
    this.sizes = [...this.querySelectorAll('input[name="fit-usual"]')].map((input) => input.value);

    if (this.dialog) {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-fit-finder-open]');
        if (!trigger || !this.isConnected) return;
        event.preventDefault();
        this.open();
      });

      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-fit-finder-close]')) this.close();
      });
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('fit-finder-open'));
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
    }

    this.querySelectorAll('[data-fit-tab]').forEach((tab) => tab.addEventListener('click', () => this.showTab(tab.dataset.fitTab)));
    this.querySelectorAll('[data-fit-unit]').forEach((button) => button.addEventListener('click', () => this.setUnit(button.dataset.fitUnit)));
    this.addEventListener('change', () => this.update());
    this.selectButton.addEventListener('click', () => this.choose());
    this.setUnit('cm');
    if (this.page) this.update();
  }

  open() {
    this.preselect();
    this.update();
    this.dialog.showModal();
    this.dialog.scrollTop = 0;
    document.documentElement.classList.add('fit-finder-open');
  }

  // Slide out, then close (instant when the user prefers less motion).
  close() {
    if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return this.dialog.close();
    this.dialog.classList.add('is-closing');
    const finish = () => {
      this.dialog.classList.remove('is-closing');
      this.dialog.close();
    };
    this.dialog.addEventListener('animationend', finish, { once: true });
    setTimeout(() => this.dialog.classList.contains('is-closing') && finish(), 600);
  }

  // Start from the size already picked on the page, if any.
  preselect() {
    if (this.querySelector('input[name="fit-usual"]:checked')) return;
    const picked = document.querySelector('#ProductSizes input[type="radio"]:checked');
    const input = picked && this.querySelector(`input[name="fit-usual"][value="${CSS.escape(picked.dataset.label)}"]`);
    if (input) input.checked = true;
  }

  showTab(name) {
    this.querySelectorAll('[data-fit-tab]').forEach((tab) => {
      const active = tab.dataset.fitTab === name;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
    this.querySelectorAll('[data-fit-panel]').forEach((panel) => (panel.hidden = panel.dataset.fitPanel !== name));
    if (this.dialog) this.dialog.scrollTop = 0;
  }

  setUnit(unit) {
    this.querySelectorAll('[data-fit-unit]').forEach((button) => {
      const active = button.dataset.fitUnit === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    this.querySelectorAll('td[data-mm]').forEach((cell) => {
      if (!cell.dataset.mm) return;
      // The chart's own inch value when it has one, otherwise converted from mm.
      const format = (mm) => (unit === 'inch' ? (mm / 25.4).toFixed(1) : (mm / 10).toFixed(1)).replace(/\.0$/, '');
      const value = unit === 'inch' && cell.dataset.in ? cell.dataset.in : cell.dataset.mm.split('-').map(Number).map(format).join('–');
      cell.textContent = `${value} ${unit === 'inch' ? 'in' : 'cm'}`;
    });
  }

  fill(template, values) {
    return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`__${key}__`, value), template || '');
  }

  // Foot length (old nallan.com calculator mapping) wins over the usual size,
  // then the model's fit moves it one size up or down, and thicker socks add
  // one more size when the product has it.
  recommend() {
    const lengthOption = this.lengthSelect?.selectedOptions[0];
    const usual = this.querySelector('input[name="fit-usual"]:checked')?.value;
    const base = lengthOption?.value || usual;
    if (!base) return null;

    const shift = this.fitScore && this.fitScore <= 2 ? 1 : this.fitScore >= 4 ? -1 : 0;
    const index = this.sizes.indexOf(base) + shift;
    const socks = this.querySelector('input[name="fit-wear"]:checked')?.value === 'socks';
    const socksUp = socks && index + 1 >= 0 && index + 1 < this.sizes.length;
    const finalIndex = socksUp ? index + 1 : index;
    return {
      size: finalIndex >= 0 && finalIndex < this.sizes.length ? this.sizes[finalIndex] : null,
      shift,
      socks,
      socksUp,
      length: lengthOption?.value ? lengthOption.dataset.length : '',
      width: lengthOption?.value ? lengthOption.dataset.width : '',
    };
  }

  productInput(size) {
    return document.querySelector(`#ProductSizes input[type="radio"][data-label="${CSS.escape(size)}"]`);
  }

  // true / false when we know the product's stock, null on the general fit guide page.
  availability(size) {
    if (!size) return false;
    if (!this.hasProduct) return null;
    if (!this.page) {
      const input = this.productInput(size);
      return Boolean(input && !input.closest('.is-unavailable'));
    }
    if (this.variants) return Boolean(this.variants[size]?.available);
    return null;
  }

  buttonLabel(size) {
    const template = !this.hasProduct || (this.page && !this.variants) ? this.strings.shop : this.strings.select;
    return this.fill(template, { SIZE: size || '—' });
  }

  update() {
    const result = this.recommend();
    const values = { MODEL: this.model };

    this.querySelectorAll('[data-fit-row]').forEach((row) => row.classList.toggle('is-active', row.dataset.fitRow === result?.size));

    if (!result) {
      this.sizeEl.textContent = '—';
      this.matchEl.hidden = true;
      this.reasonEl.textContent = this.strings.empty;
      this.noteEl.hidden = true;
      this.widthEl.hidden = true;
      this.selectButton.disabled = true;
      this.selectButton.textContent = this.buttonLabel();
      return;
    }

    const reasons = [];
    if (result.length) reasons.push(this.fill(this.strings.reasonLength, { LENGTH: result.length }));
    if (result.shift > 0) reasons.push(this.fill(this.strings.reasonSmall, values));
    else if (result.shift < 0) reasons.push(this.fill(this.strings.reasonLarge, values));
    else if (!result.length && !result.socksUp) reasons.push(this.fill(this.strings.reasonTrue, values));
    this.reasonEl.textContent = reasons.join(' ');

    this.noteEl.hidden = false;
    const note = result.socksUp ? this.strings.noteSocks : result.socks ? this.strings.noteSocksRoom : this.strings.noteBarefoot;
    this.noteEl.textContent = this.fill(note, values);
    this.widthEl.hidden = !result.width;
    this.widthEl.textContent = this.fill(this.strings.width, { WIDTH: result.width });

    const available = this.availability(result.size);
    this.sizeEl.textContent = result.size || '—';
    this.matchEl.hidden = available === null;
    this.matchEl.classList.toggle('is-warning', !available);
    this.matchEl.querySelector('span').textContent = !result.size
      ? this.strings.notAvailable
      : available
        ? this.strings.goodMatch
        : this.strings.soldOut;
    this.selectButton.disabled = available === null ? !result.size : !available;
    this.selectButton.textContent = this.buttonLabel(result.size);
  }

  choose() {
    const result = this.recommend();
    if (!this.hasProduct && !this.page) {
      if (!result?.size) return;
      // On a collection: pick that size in the size bar instead of leaving the page.
      const bar = document.querySelector('[data-size-bar]');
      const button =
        bar?.querySelector(`[data-size-local="${CSS.escape(result.size)}"]`) ||
        [...(bar?.querySelectorAll('a.collection-size-bar__size') || [])].find((link) => link.textContent.trim() === result.size);
      if (button) {
        if (!button.classList.contains('is-active')) button.click();
        this.close();
        return;
      }
      window.location.assign(this.dataset.shopUrl.replace('__SIZE__', encodeURIComponent(result.size)));
      return;
    }
    if (this.page) {
      if (!result?.size) return;
      const variant = this.variants?.[result.size];
      window.location.assign(
        variant
          ? `${this.dataset.productUrl}?variant=${variant.id}`
          : this.dataset.shopUrl.replace('__SIZE__', encodeURIComponent(result.size))
      );
      return;
    }
    const input = result?.size && this.productInput(result.size);
    if (!input) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
  }
}

if (!customElements.get('fit-finder')) customElements.define('fit-finder', FitFinder);
